import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", name: "", password: "", role: "admin" };

export default function AdminUsersManager() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ email: a.email, name: a.name, password: "", role: a.role, is_active: a.is_active });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admins", form);
        toast.success(t("admin.admins.toastCreated"));
      } else {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.put(`/admins/${editing}`, payload);
        toast.success(t("admin.admins.toastUpdated"));
      }
      close(); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admins/${a.id}`, { is_active: !a.is_active });
      toast.success(a.is_active ? t("admin.admins.toastDeactivated") : t("admin.admins.toastActivated"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/admins/${id}`);
      toast.success(t("admin.admins.toastDeleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admins-manager" className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.admins.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.admins.title")}</h1>
        </div>
        <Button onClick={openNew} data-testid="new-admin-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
          <Plus size={16} className="mr-2" /> {t("admin.admins.newBtn")}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-beige-light border-b border-slate-200">
              <th className="px-4 py-3 font-medium text-slate-900">{t("admin.admins.colName")}</th>
              <th className="px-4 py-3 font-medium text-slate-900">{t("admin.admins.colEmail")}</th>
              <th className="px-4 py-3 font-medium text-slate-900">{t("admin.admins.colRole")}</th>
              <th className="px-4 py-3 font-medium text-slate-900">{t("admin.admins.colStatus")}</th>
              <th className="px-4 py-3 font-medium text-slate-900 text-right">{t("admin.common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`} className="border-b border-slate-100 hover:bg-beige-light/50">
                <td className="px-4 py-3 text-slate-900 font-medium">
                  <div className="flex items-center gap-2">
                    {a.role === "super_admin" && <ShieldCheck size={14} className="text-bordeaux" />}
                    {a.name || "—"}
                    {a.id === me?.id && <span className="text-xs text-slate-400 ml-2">{t("admin.admins.you")}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{a.email}</td>
                <td className="px-4 py-3">
                  <Badge className={a.role === "super_admin" ? "bg-bordeaux-light text-bordeaux rounded-sm" : "bg-slate-100 text-slate-700 rounded-sm"}>
                    {a.role === "super_admin" ? t("admin.admins.roleSuper") : t("admin.admins.roleAdmin")}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Switch checked={a.is_active} disabled={a.id === me?.id} onCheckedChange={() => toggleActive(a)} data-testid={`admin-toggle-${a.id}`} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(a)} data-testid={`admin-edit-${a.id}`} className="p-2 hover:bg-slate-100 rounded-sm text-slate-600 hover:text-bordeaux">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => setConfirmId(a.id)} disabled={a.id === me?.id} data-testid={`admin-delete-${a.id}`}
                      className="p-2 hover:bg-red-50 rounded-sm text-slate-600 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md rounded-sm" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("admin.admins.dialogNew") : t("admin.admins.dialogEdit")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editing === "new" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t("admin.admins.fieldEmail")}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" className="rounded-sm border-slate-300" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t("admin.admins.fieldName")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" className="rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                {t("admin.admins.fieldPassword")} {editing !== "new" && t("admin.admins.fieldPasswordHint")}
              </Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" className="rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t("admin.admins.fieldRole")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="admin-role-select" className="rounded-sm border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("admin.admins.roleAdmin")}</SelectItem>
                  <SelectItem value="super_admin">{t("admin.admins.roleSuper")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="rounded-sm border-slate-300 hover:bg-beige-light hover:text-slate-900">{t("admin.common.cancel")}</Button>
            <Button onClick={save} disabled={busy} data-testid="admin-save-btn" className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm">
              {busy ? t("admin.common.saving") : t("admin.common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.common.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.common.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">{t("admin.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-delete-confirm" className="bg-red-600 hover:bg-red-700">
              {t("admin.common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
